import type { ScormApiBridge } from './ScormApiBridge'
import { cmiTimespanToMs, iso8601DurationToMs, msToCmiTimespan, msToIso8601Duration } from './durations'
import { SCORM12_INTERACTION_CHILDREN, SCORM12_INTERACTION_KEY, scorm12InteractionWrite, scorm12ResponsesTo2004 } from './scorm12-interactions'
import { ErrorCode } from './types'

/** Codes d'erreur du runtime SCORM 1.2 (espace distinct des codes 2004). */
const SCORM12_ERROR_STRINGS: Record<number, string> = {
  0: 'No error',
  101: 'General exception',
  201: 'Invalid argument error',
  202: 'Element cannot have children',
  203: 'Element not an array - cannot have count',
  301: 'Not initialized',
  401: 'Not implemented error',
  402: 'Invalid set value, element is a keyword',
  403: 'Element is read only',
  404: 'Element is write only',
  405: 'Incorrect data type',
}

/** Traduction des codes d'erreur 2004 du bridge vers l'espace 1.2. */
const SCORM2004_TO_12_ERRORS: Record<number, number> = {
  [ErrorCode.NO_ERROR]: 0,
  [ErrorCode.GENERAL_EXCEPTION]: 101,
  [ErrorCode.GENERAL_INITIALIZATION_FAILURE]: 101,
  [ErrorCode.ALREADY_INITIALIZED]: 101,
  [ErrorCode.CONTENT_INSTANCE_TERMINATED]: 101,
  [ErrorCode.GENERAL_TERMINATION_FAILURE]: 101,
  // 1.2 n'a pas de notion de "terminated" : après LMSFinish, l'API n'est
  // simplement plus initialisée (301).
  [ErrorCode.TERMINATE_BEFORE_INIT]: 301,
  [ErrorCode.TERMINATE_AFTER_TERMINATE]: 301,
  [ErrorCode.GET_BEFORE_INIT]: 301,
  [ErrorCode.GET_AFTER_TERMINATE]: 301,
  [ErrorCode.SET_BEFORE_INIT]: 301,
  [ErrorCode.SET_AFTER_TERMINATE]: 301,
  [ErrorCode.COMMIT_BEFORE_INIT]: 301,
  [ErrorCode.COMMIT_AFTER_TERMINATE]: 301,
  [ErrorCode.GENERAL_ARGUMENT_ERROR]: 201,
  [ErrorCode.UNDEFINED_DATA_MODEL_ELEMENT]: 401,
  [ErrorCode.UNIMPLEMENTED_DATA_MODEL_ELEMENT]: 401,
  [ErrorCode.DATA_MODEL_ELEMENT_IS_READ_ONLY]: 403,
  [ErrorCode.DATA_MODEL_ELEMENT_IS_WRITE_ONLY]: 404,
  [ErrorCode.DATA_MODEL_ELEMENT_TYPE_MISMATCH]: 405,
  [ErrorCode.DATA_MODEL_ELEMENT_VALUE_OUT_OF_RANGE]: 405,
}

/** Éléments cmi.core.* 1.2 dont la valeur est identique côté 2004. */
const SCORM12_TO_2004_KEYS: Record<string, string> = {
  'cmi.core.student_id': 'cmi.learner_id',
  'cmi.core.student_name': 'cmi.learner_name',
  'cmi.core.lesson_location': 'cmi.location',
  'cmi.core.credit': 'cmi.credit',
  'cmi.core.entry': 'cmi.entry',
  'cmi.core.lesson_mode': 'cmi.mode',
  'cmi.core.exit': 'cmi.exit',
  'cmi.core.score.raw': 'cmi.score.raw',
  'cmi.core.score.min': 'cmi.score.min',
  'cmi.core.score.max': 'cmi.score.max',
}

/**
 * Projection du lesson_status 1.2 (axe unique) sur le completion_status 2004.
 * Seuls passed/failed portent un signal de réussite (voir setSuccess) ; les
 * autres valeurs remettent l'axe success à plat et laissent l'API dériver du
 * score, à l'image du bridge 2004 natif.
 */
const LESSON_STATUS_TO_2004: Record<string, { completion: string, setSuccess: 'passed' | 'failed' | null }> = {
  passed: { completion: 'completed', setSuccess: 'passed' },
  completed: { completion: 'completed', setSuccess: null },
  failed: { completion: 'completed', setSuccess: 'failed' },
  incomplete: { completion: 'incomplete', setSuccess: null },
  browsed: { completion: 'incomplete', setSuccess: null },
  'not attempted': { completion: 'not attempted', setSuccess: null },
}

const SCORM12_CORE_CHILDREN = 'student_id,student_name,lesson_location,credit,lesson_status,entry,score,total_time,lesson_mode,exit,session_time'

// SCORM 1.2 adapter: traduit les noms d'éléments (cmi.core.* → modèle 2004),
// les formats de valeurs (CMITimespan ↔ ISO 8601, lesson_status ↔ deux axes)
// et les codes d'erreur (espace 1.2) avant de déléguer au bridge 2004.
export function createScorm12Adapter(bridge: ScormApiBridge) {
  // Erreur posée par l'adapter lui-même (validations propres à 1.2) ; à défaut,
  // le dernier code du bridge est traduit vers l'espace 1.2.
  let adapterError: number | null = null
  // Les réponses 1.2 (`a,b`, `t`…) partent au serveur au format 2004 (#158).
  bridge.setInteractionFormat(scorm12ResponsesTo2004)

  const lastErrorCode = (): number => {
    if (adapterError !== null) return adapterError
    return SCORM2004_TO_12_ERRORS[bridge.getLastErrorCode()] ?? 101
  }

  return {
    LMSInitialize: (param: string = '') => {
      adapterError = null
      return bridge.Initialize(param)
    },

    LMSFinish: (param: string = '') => {
      adapterError = null
      return bridge.Terminate(param)
    },

    LMSGetValue: (key: string): string => {
      adapterError = null

      if (key === 'cmi.core.lesson_status') {
        const completion = bridge.GetValue('cmi.completion_status')
        if (bridge.getLastErrorCode() !== ErrorCode.NO_ERROR) return ''
        const success = bridge.GetValue('cmi.success_status')
        if (success === 'passed' || success === 'failed') return success
        if (completion === 'completed' || completion === 'incomplete') return completion
        return 'not attempted'
      }

      if (key === 'cmi.core.total_time') {
        const iso = bridge.GetValue('cmi.total_time')
        if (bridge.getLastErrorCode() !== ErrorCode.NO_ERROR) return ''
        return msToCmiTimespan(iso8601DurationToMs(iso))
      }

      if (key === 'cmi.core.session_time') {
        adapterError = bridge.isInitialized() ? 404 : 301
        return ''
      }

      // Les interactions sont en écriture seule en 1.2, leurs compteurs exceptés.
      if (SCORM12_INTERACTION_KEY.test(key) && !key.endsWith('._count')) {
        adapterError = bridge.isInitialized() ? 404 : 301
        return ''
      }

      if (key === 'cmi.core._children' || key === 'cmi.core.score._children' || key === 'cmi.interactions._children') {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return ''
        }
        if (key === 'cmi.interactions._children') return SCORM12_INTERACTION_CHILDREN
        return key === 'cmi.core._children' ? SCORM12_CORE_CHILDREN : 'raw,min,max'
      }

      return bridge.GetValue(SCORM12_TO_2004_KEYS[key] ?? key)
    },

    LMSSetValue: (key: string, value: string): string => {
      adapterError = null

      if (key === 'cmi.core.lesson_status') {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return 'false'
        }
        const mapped = LESSON_STATUS_TO_2004[value]
        if (!mapped) {
          adapterError = 405
          return 'false'
        }
        if (bridge.SetValue('cmi.completion_status', mapped.completion) !== 'true') return 'false'
        // Seuls passed/failed portent un verdict ; sinon on remet l'axe success
        // à plat pour ne pas réémettre un passed/failed devenu obsolète.
        if (mapped.setSuccess === null) {
          bridge.resetSuccessStatus()
          return 'true'
        }
        return bridge.SetValue('cmi.success_status', mapped.setSuccess)
      }

      if (key === 'cmi.core.session_time') {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return 'false'
        }
        const ms = cmiTimespanToMs(value)
        if (ms === null) {
          adapterError = 405
          return 'false'
        }
        return bridge.SetValue('cmi.session_time', msToIso8601Duration(ms))
      }

      // Les mots-clés (`_children`, `_count`) se lisent, ils ne s'écrivent pas.
      if (key === 'cmi.core.total_time' || key.endsWith('._children') || key.endsWith('._count')) {
        if (!bridge.isInitialized()) {
          adapterError = 301
          return 'false'
        }
        adapterError = 403
        return 'false'
      }

      const interaction = scorm12InteractionWrite(key, value)
      if (interaction === null) {
        adapterError = bridge.isInitialized() ? 405 : 301
        return 'false'
      }
      if (interaction) return bridge.SetValue(interaction.key, interaction.value)

      return bridge.SetValue(SCORM12_TO_2004_KEYS[key] ?? key, value)
    },

    LMSCommit: (param: string = '') => {
      adapterError = null
      return bridge.Commit(param)
    },

    LMSGetLastError: () => String(lastErrorCode()),
    LMSGetErrorString: (code: string) => SCORM12_ERROR_STRINGS[Number.parseInt(code, 10)] ?? 'Unknown error',
    LMSGetDiagnostic: (code: string) => SCORM12_ERROR_STRINGS[Number.parseInt(code, 10)] ?? 'Unknown error',
  }
}
